/**
 * Duplicate / Overlap detection (section 21e) — a pure, client-side similarity
 * heuristic across workstreams. It surfaces "you two might be doing overlapping
 * work — talk to each other" as a COLLABORATION prompt, never as an enforcement
 * or a duplicate-work accusation, and never with any per-person metric.
 *
 * Similarity signal (0..1):
 *   - shared `systemsAffected` (Jaccard over the two systems sets) — weighted 0.7
 *   - shared title keywords (token overlap of titles)               — weighted 0.3
 * A pair surfaces when it shares at least one system OR enough title keywords.
 *
 * Pure TS — no React.
 */

import type { WorkstreamSummary } from '../workstreams/types';
import { tokenOverlap } from './intelligenceModel';
import type { WorkstreamOverlap } from './types';

const SYSTEM_WEIGHT = 0.7;
const TITLE_WEIGHT = 0.3;
/** Minimum blended score for a pair to surface. */
const MIN_SCORE = 0.1;
/** Minimum title token overlap ratio to count as a keyword match on its own. */
const MIN_TITLE_RATIO = 0.34;

/** Lowercased, de-duped set of a workstream's affected systems. */
function systemSet(ws: WorkstreamSummary): Set<string> {
  return new Set((ws.systemsAffected ?? []).map((s) => s.trim().toLowerCase()).filter(Boolean));
}

/** Jaccard similarity + the shared members (original casing from `a`) of two sets. */
function systemJaccard(
  a: WorkstreamSummary,
  b: WorkstreamSummary,
): { shared: string[]; ratio: number } {
  const sa = systemSet(a);
  const sb = systemSet(b);
  if (sa.size === 0 || sb.size === 0) return { shared: [], ratio: 0 };
  const sharedLower = new Set<string>();
  for (const s of sa) if (sb.has(s)) sharedLower.add(s);
  // Preserve original casing from a's list for display.
  const shared = (a.systemsAffected ?? []).filter((s) =>
    sharedLower.has(s.trim().toLowerCase()),
  );
  const union = new Set([...sa, ...sb]).size;
  return { shared, ratio: union === 0 ? 0 : sharedLower.size / union };
}

/** De-duped list of engineers + POCs worth connecting for a pair. */
function suggestedContacts(a: WorkstreamSummary, b: WorkstreamSummary): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const who of [a.engineer, a.poc, b.engineer, b.poc]) {
    const t = (who ?? '').trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

export interface DetectOverlapOptions {
  /** Minimum blended score to include (defaults to {@link MIN_SCORE}). */
  minScore?: number;
  /** Max findings to return (defaults to unbounded). */
  limit?: number;
}

/**
 * Detects possible overlaps across a set of workstreams. Returns findings
 * newest-similarity-first (highest score first), each with the shared systems,
 * shared keywords, and the people worth connecting.
 */
export function detectOverlaps(
  workstreams: WorkstreamSummary[],
  options: DetectOverlapOptions = {},
): WorkstreamOverlap[] {
  const minScore = options.minScore ?? MIN_SCORE;
  const findings: WorkstreamOverlap[] = [];

  for (let i = 0; i < workstreams.length; i++) {
    for (let j = i + 1; j < workstreams.length; j++) {
      const a = workstreams[i];
      const b = workstreams[j];
      const sys = systemJaccard(a, b);
      const title = tokenOverlap(a.title ?? '', b.title ?? '');

      const hasSystemMatch = sys.shared.length > 0;
      const hasTitleMatch = title.ratio >= MIN_TITLE_RATIO;
      if (!hasSystemMatch && !hasTitleMatch) continue;

      const score = Math.min(
        1,
        SYSTEM_WEIGHT * sys.ratio + TITLE_WEIGHT * title.ratio,
      );
      if (score < minScore) continue;

      findings.push({
        a,
        b,
        sharedSystems: sys.shared,
        sharedKeywords: title.shared,
        score,
        suggestedContacts: suggestedContacts(a, b),
      });
    }
  }

  findings.sort((x, y) => y.score - x.score);
  return options.limit ? findings.slice(0, options.limit) : findings;
}
